import type { RegisteredRouter } from '@tanstack/react-router';
import type { z } from 'zod';
import {
  INITIAL_ASSET_LISTING_STATE,
  INITIAL_ASSET_QUERY,
  ROOT_FOLDER_ID,
  withFolderOpened,
  withQueryReset,
  withSearch,
  withSort,
  withTypeToggled,
  withViewMode,
  type AssetListingState,
  type AssetListingStore,
  type AssetListingStoreDeps,
  type AssetType,
  type ProcessingState,
  type SortDirection,
  type SortField,
} from '@ael/shared-components-library';

import { mediaSearchSchema, type MediaSearch } from '../../routing/schemas';

const MEDIA_ROUTE_ID = '/_private/_home/media/{-$folderId}';

/** Where the dashboard's library remembers its last folder, read back by the media route on entry. */
export const MEDIA_STORAGE_SCOPE = 'dashboard';

/**
 * The URL's half of the mapping. Anything the URL leaves out is the library's own default, and a
 * path without a folder is the root -- never `null` -- so a plain /media link opens the root.
 */
const fromLocation = (folderId: string | undefined, search: MediaSearch): AssetListingState => {
  const [field, direction] = (search.sort?.split('-') ?? []) as [SortField?, SortDirection?];

  return {
    viewMode: search.view ?? INITIAL_ASSET_LISTING_STATE.viewMode,
    activeFolderId: folderId ?? ROOT_FOLDER_ID,
    search: search.q ?? '',
    // The schema has already dropped any value the library does not know.
    types: (search.types?.split(',') ?? []) as AssetType[],
    states: (search.states?.split(',') ?? []) as ProcessingState[],
    sort: field && direction ? { field, direction } : INITIAL_ASSET_QUERY.sort,
  };
};

const toSearch = (state: AssetListingState): z.input<typeof mediaSearchSchema> => ({
  view: state.viewMode === INITIAL_ASSET_LISTING_STATE.viewMode ? undefined : state.viewMode,
  q: state.search === '' ? undefined : state.search,
  types: state.types.length === 0 ? undefined : state.types.join(','),
  states: state.states.length === 0 ? undefined : state.states.join(','),
  sort:
    state.sort.field === INITIAL_ASSET_QUERY.sort.field && state.sort.direction === INITIAL_ASSET_QUERY.sort.direction
      ? undefined
      : `${state.sort.field}-${state.sort.direction}`,
});

// The root has an id of its own, but its URL is the bare /media, without a guid of zeros in it.
const toFolderParam = (state: AssetListingState): string | undefined =>
  state.activeFolderId === ROOT_FOLDER_ID || state.activeFolderId === null ? undefined : state.activeFolderId;

/**
 * Keeps the asset library's listing state in the browser url: the folder in the path, and search,
 * filters, sort and view in the query string.
 *
 * Opening a folder adds a history entry, so Back returns to the folder before it; everything else
 * -- a search typed a letter at a time, a filter, the sort, the view -- replaces the current one.
 *
 * Back/Forward lands on a listing the user did not pick here, so the selection is cleared with it.
 * Our own `navigate` is a PUSH or a REPLACE and leaves it alone.
 */
export function createAssetListingRouterStore(
  router: RegisteredRouter,
  deps: AssetListingStoreDeps
): AssetListingStore {
  const listeners = new Set<() => void>();
  const notify = () => {
    listeners.forEach((listener) => {
      listener();
    });
  };

  // One object per URL: `useSyncExternalStore` re-renders whenever the snapshot is a different
  // object, so parsing a fresh one on every read would never settle.
  let snapshot: { key: string; state: AssetListingState } | undefined;

  /*
   * What was set but has not reached the URL yet. A navigation resolves later, and until it does
   * the URL still says what it said before -- so without this a second change made in the meantime
   * would be worked out from the first one's starting point, and undo it.
   */
  let pending: AssetListingState | undefined;
  let unsubscribeRouter: (() => void) | undefined;

  const getState = (): AssetListingState => {
    if (pending) return pending;

    const { pathname, searchStr } = router.state.location;
    const key = pathname + searchStr;
    if (snapshot?.key !== key) {
      const { folderId } = router.state.matches.find((match) => match.routeId === MEDIA_ROUTE_ID)?.params ?? {};
      // The search is re-parsed rather than read off the typed one, which is a union of every route's.
      snapshot = { key, state: fromLocation(folderId, mediaSearchSchema.parse(router.state.location.search)) };
    }
    return snapshot.state;
  };

  const navigate = (next: AssetListingState, replace: boolean) => {
    pending = next;
    notify();

    void router.navigate({
      to: '/media/{-$folderId}',
      params: { folderId: toFolderParam(next) },
      search: (prev: Record<string, unknown>) => ({ ...prev, ...toSearch(next) }),
      // Survives Back/Forward and reload, so the media route can tell a bare /media the user chose
      // here (the root) from one they arrived at from elsewhere, which reopens the last folder.
      state: (prev) => ({ ...prev, fromAssetLibrary: true }),
      replace,
    });
  };

  /**
   * The path changes, so it is a new history entry. Re-opening the folder already open only clears
   * the search, if there is one, in place.
   */
  const goToFolder = (next: AssetListingState) => {
    const current = getState();
    const samePath = toFolderParam(next) === toFolderParam(current);
    if (samePath && next.search === current.search) return;
    navigate(next, samePath);
  };

  /** Only the query string changes, so the current entry is replaced. */
  const replaceQuery = (next: AssetListingState) => {
    // A transition that changed nothing hands back the same state.
    if (next === getState()) return;
    navigate(next, true);
  };

  return {
    getState,
    subscribe: (listener) => {
      listeners.add(listener);
      // Opened once for every listener, so a Back clears the selection once. Only while somebody
      // listens: StrictMode builds the store twice and drops one, which would leave the dropped
      // one's subscriptions open.
      if (listeners.size === 1) {
        // `onResolved` also fires for Back/Forward and pasted links, not only for our own navigate.
        const unsubscribeResolved = router.subscribe('onResolved', () => {
          pending = undefined;
          notify();
        });
        const unsubscribeHistory = router.history.subscribe(({ action }) => {
          if (action.type === 'BACK' || action.type === 'FORWARD' || action.type === 'GO') deps.clearSelection();
        });
        unsubscribeRouter = () => {
          unsubscribeResolved();
          unsubscribeHistory();
        };
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          unsubscribeRouter?.();
          unsubscribeRouter = undefined;
        }
      };
    },
    /*
     * KEEP IN SYNC with `createLocalAssetListingStore` in
     * Shared/front-end/libs/shared-components-library/src/components/organisms/AssetLibrary/domain/client-state/assetListingStore.ts,
     * the library's default store.
     */
    actions: {
      setViewMode: (viewMode) => {
        replaceQuery(withViewMode(getState(), viewMode));
      },
      openFolder: (folderId) => {
        goToFolder(withFolderOpened(getState(), folderId));
        deps.clearSelection();
        deps.rememberFolder(folderId);
      },
      setSearch: (search) => {
        replaceQuery(withSearch(getState(), search));
        deps.clearSelection();
      },
      toggleType: (type) => {
        replaceQuery(withTypeToggled(getState(), type));
        deps.clearSelection();
      },
      setSort: (sort) => {
        replaceQuery(withSort(getState(), sort));
      },
      resetQuery: () => {
        replaceQuery(withQueryReset(getState()));
        deps.clearSelection();
      },
    },
  };
}
